if (!customElements.get('simple-slider')) {
  class SimpleSlider extends HTMLElement {
    constructor() {
      super();

      // Slider elements
      this.sliderList = this.querySelector('[data-list]');
      this.left = this.querySelector('[data-left]');
      this.right = this.querySelector('[data-right]');
      this.dots = this.querySelectorAll('[data-dot]');
      this.controls = this.querySelector('[data-controls]');

      // Initial state
      this.slide = this.sliderList?.children[0] || null;
      this.currentIndex = 0;
      this.isMouseDown = false;
      this.startX = 0;
      this.dragged = false; // Track whether dragging occurred

      // Full-width mode: slider is used without a container and can show many
      // slides at once, so navigation is tracked by scroll progress instead of
      // the per-slide IntersectionObserver.
      this.fullWidth = this.hasAttribute('data-full-width');
      this.scrollRaf = null;
      this.onScroll = this.handleScroll.bind(this);

      this.setupEventListeners();

      this.setupObserver();

      if (!this.fullWidth) {
        this.visibilityNavigation();
      }
    }

    setupEventListeners() {
      this.left?.addEventListener('click', () => this.scrollLeft());
      this.right?.addEventListener('click', () => this.scrollRight());

      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => {
          this.sliderList.scrollLeft = this.slide.clientWidth * index;
        });
      });

      if (window.innerWidth >= 750) {
        // Mouse drag support
        this.sliderList.addEventListener('mousedown', this.onMouseDown.bind(this));
        this.sliderList.addEventListener('mousemove', this.onMouseMove.bind(this));
        this.sliderList.addEventListener('mouseup', this.onMouseUp.bind(this));
        this.sliderList.addEventListener('mouseleave', this.onMouseUp.bind(this));

        // Prevent link navigation on drag
        this.sliderList.querySelectorAll('a').forEach((link) => {
          link.addEventListener('click', (event) => {
            if (this.dragged) {
              event.preventDefault();
            }
          });
        });

        // Prevent play video on drag
        this.sliderList.querySelectorAll('custom-video').forEach((video) => {
          video.addEventListener('click', (event) => {
            if (this.dragged) {
              event.stopImmediatePropagation();
            }
          });
        });
      }
    }

    onMouseDown(event) {
      this.isMouseDown = true;
      this.startX = event.pageX - this.sliderList.offsetLeft;
      this.dragged = false; // Reset dragged state on new drag
      this.sliderList.style.cursor = 'grabbing'; // Change cursor to indicate dragging
    }

    onMouseMove(event) {
      if (!this.isMouseDown) return;
      const moveX = event.pageX - this.sliderList.offsetLeft;
      const distance = moveX - this.startX;

      // If movement is significant, set dragged to true
      if (Math.abs(distance) > 5) {
        this.dragged = true;
      }

      if (distance < 0) {
        this.scrollRight();
      } else {
        this.scrollLeft();
      }
    }

    onMouseUp() {
      this.isMouseDown = false;
      this.sliderList.style.cursor = 'grab'; // Reset cursor
    }

    scrollLeft() {
      this.sliderList.scrollLeft -= this.slide.clientWidth;
    }

    scrollRight() {
      this.sliderList.scrollLeft += this.slide.clientWidth;
    }

    setupObserver() {
      if (this.fullWidth) {
        this.sliderList.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll);
        this.updateFromScroll();
        return;
      }

      const sliderScrollObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.currentIndex = parseInt(entry.target.dataset.index, 10);
              this.visibilityNavigation();
              this.visibilityDots();
            }
          });
        },
        {
          threshold: 0.6, // Adjusted to ensure proper detection of slide visibility
        }
      );

      // Assign `data-index` to each slide for identification
      [...this.sliderList.children].forEach((slide, index) => {
        slide.dataset.index = index;
        sliderScrollObserver.observe(slide);
      });
    }

    visibilityNavigation() {
      const size = this.sliderList.children.length - 1;
      // Check if at the first or last slide
      if (this.currentIndex === 0) {
        this.left?.setAttribute('data-active', 'false');
      } else {
        this.left?.setAttribute('data-active', 'true');
      }

      if (this.currentIndex === size) {
        this.right?.setAttribute('data-active', 'false');
      } else {
        this.right?.setAttribute('data-active', 'true');
      }
    }

    visibilityDots() {
      this.dots.forEach((dot) => dot?.removeAttribute('data-active'));
      this.dots[this.currentIndex]?.setAttribute('data-active', '');
    }

    handleScroll() {
      if (this.scrollRaf) return;
      this.scrollRaf = requestAnimationFrame(() => {
        this.scrollRaf = null;
        this.updateFromScroll();
      });
    }

    updateFromScroll() {
      const list = this.sliderList;
      const step = this.slide?.clientWidth || 1;
      const maxScroll = list.scrollWidth - list.clientWidth;

      // Nothing to scroll to: dots and arrows would be dead controls.
      // Sub-pixel layout rounding can leave a fraction of overflow, so allow 1px.
      this.controls?.toggleAttribute('data-hidden', maxScroll <= 1);

      // When many slides are visible at once there are fewer scroll positions
      // than slides, so the slider counts reachable pages (one slide per scroll
      // step) instead of slides.
      // Ceil, not round: the remaining scroll is rarely a whole slide (the edge
      // slides sit flush against the page-width edge), and rounding it down
      // would drop the last reachable page.
      const pages = Math.max(1, Math.ceil(maxScroll / step) + 1);

      // Dots are optional; only as many as there are pages are shown.
      this.dots.forEach((dot, index) => {
        dot.toggleAttribute('data-hidden', index >= pages);
      });

      let index = Math.round(list.scrollLeft / step);
      if (list.scrollLeft >= maxScroll - 1) index = pages - 1;
      this.currentIndex = Math.min(Math.max(index, 0), pages - 1);
      this.visibilityDots();

      // Arrows follow the page index rather than the raw scrollLeft, which can
      // leave a small unreachable trailing gap.
      this.left?.setAttribute('data-active', this.currentIndex > 0 ? 'true' : 'false');
      this.right?.setAttribute('data-active', this.currentIndex < pages - 1 ? 'true' : 'false');
    }
  }

  customElements.define('simple-slider', SimpleSlider);
}
